import type { Series } from "./SimulationGraph";

// How a signal is drawn on the 2D graph, to compare it with signals at other
// times or of other magnitudes: shown = value × scale + offset, at time + shift.
export interface SignalTransform {
  timeShift: number; // s
  scale: number;
  offset: number;
}

export const IDENTITY_TRANSFORM: SignalTransform = { timeShift: 0, scale: 1, offset: 0 };

export const isIdentityTransform = (transform: SignalTransform | undefined): boolean =>
  !transform || (transform.timeShift === 0 && transform.scale === 1 && transform.offset === 0);

// Only well-formed transforms survive, e.g. when loaded from storage.
export const sanitizeTransforms = (value: unknown): Record<string, SignalTransform> => {
  if (!value || typeof value !== "object") {
    return {};
  }

  return Object.fromEntries(Object.entries(value).filter(([, transform]) => (
    transform && typeof transform === "object"
    && ["timeShift", "scale", "offset"].every((key) => Number.isFinite(transform[key]))
  )));
};

const format = (value: number) => `${Number(value.toPrecision(6))}`;

// Short description such as "×0.1 +2 · t +0.5 s".
export const describeTransform = (transform: SignalTransform): string => [
  transform.scale !== 1 || transform.offset !== 0
    ? [
      transform.scale !== 1 ? `×${format(transform.scale)}` : "",
      transform.offset !== 0 ? `${transform.offset > 0 ? "+" : "−"}${format(Math.abs(transform.offset))}` : "",
    ].filter(Boolean).join(" ")
    : "",
  transform.timeShift !== 0 ? `t ${transform.timeShift > 0 ? "+" : "−"}${format(Math.abs(transform.timeShift))} s` : "",
].filter(Boolean).join(" · ");

export interface ValueRange {
  min: number;
  max: number;
}

// Reduced rather than spread: long series would overflow the call stack.
export const getValueRange = (series: Series): ValueRange | null => series.y.length === 0
  ? null
  : series.y.reduce((range, value) => ({ min: Math.min(range.min, value), max: Math.max(range.max, value) }), { min: Infinity, max: -Infinity });

// Scale and offset that draw the source range over the target range. A flat
// source is centred on the target instead.
export const matchRange = (source: ValueRange, target: ValueRange): Pick<SignalTransform, "scale" | "offset"> => {
  const sourceSpan = source.max - source.min;

  if (sourceSpan === 0) {
    return { scale: 1, offset: (target.min + target.max) / 2 - source.min };
  }

  const scale = (target.max - target.min) / sourceSpan;
  return { scale, offset: target.min - source.min * scale };
};
